import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Empty, Layout, Popconfirm, Space, Spin, Table, Typography } from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { getApiErrorMessage, serverFetch } from '@api/server';
import SpaceSearch from './space-search';

const { Content } = Layout;
const { Text, Title } = Typography;

export const getSpaces = () => serverFetch('space');

export const getSpace = (id) => serverFetch(`space/${id}`);

export const createSpace = (data) => serverFetch('space', {
  method: 'POST',
  bodyData: data,
});

export const updateSpace = (data) => serverFetch('space', {
  method: 'PUT',
  bodyData: data,
});

export const deleteSpace = (id) => serverFetch(`space/${id}`, {
  method: 'DELETE',
});

export const searchSpaceChunks = (spaceId, text) => serverFetch('articlechunks', {
  queryParams: {
    spaceid: spaceId,
    text,
  },
});

const Spaces = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [spaces, setSpaces] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [deletingSpaceId, setDeletingSpaceId] = useState(null);
  const [searchSpace, setSearchSpace] = useState(null);

  const loadSpaces = useCallback(async () => {
    setError('');
    setIsLoading(true);
    try {
      const result = await getSpaces();
      setSpaces(result || []);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSpaces();
  }, [loadSpaces, location.state?.spacesRefreshKey]);

  const handleDeleteSpace = async (spaceId) => {
    setError('');
    setDeletingSpaceId(spaceId);
    try {
      await deleteSpace(spaceId);
      setSpaces((currentSpaces) => (
        currentSpaces.filter((space) => space.id !== spaceId)
      ));
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setDeletingSpaceId(null);
    }
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'id',
      width: 90,
    },
    {
      title: 'Название',
      dataIndex: 'name',
      render: (name) => <Text strong>{name}</Text>,
    },
    {
      title: '',
      key: 'actions',
      width: 88,
      render: (_, space) => (
        <span className="table-row-action table-row-actions" onClick={(event) => event.stopPropagation()}>
          <Button
            type="text"
            icon={<SearchOutlined />}
            aria-label="Поиск"
            title="Поиск"
            onClick={() => setSearchSpace(space)}
          />
          <Popconfirm
            title="Удалить пространство?"
            description="Статьи пространства останутся в базе."
            okText="Удалить"
            cancelText="Отмена"
            okButtonProps={{ danger: true, loading: deletingSpaceId === space.id }}
            onConfirm={() => handleDeleteSpace(space.id)}
          >
            <Button
              danger
              type="text"
              icon={<DeleteOutlined />}
              aria-label="Удалить"
              title="Удалить"
              loading={deletingSpaceId === space.id}
            />
          </Popconfirm>
        </span>
      ),
    },
  ];

  return (
    <Content className="users-page">
      <div className="users-panel">
        <div className="panel-title users-title">
          <Space direction="vertical" size={0}>
            <Title level={5}>Пространства</Title>
            <Text type="secondary">{spaces.length}</Text>
          </Space>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadSpaces}>
              Обновить
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/spaces/new')}>
              Создать
            </Button>
          </Space>
        </div>

        {error && (
          <Alert className="panel-alert" type="error" message={error} showIcon />
        )}

        <Spin spinning={isLoading}>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={spaces}
            locale={{ emptyText: <Empty description="Пространств пока нет" /> }}
            pagination={false}
            onRow={(space) => ({
              onClick: () => navigate(`/spaces/${space.id}`),
            })}
          />
        </Spin>
      </div>

      <SpaceSearch
        space={searchSpace}
        open={Boolean(searchSpace)}
        onClose={() => setSearchSpace(null)}
      />
    </Content>
  );
};

export default Spaces;
